// THE COLLAPSIBLE PORTAL RAIL (Leads centre polish, C). Pure: the stored choice, when the collapse applies, and where a tooltip or a flyout sits.
//
// A viewer can fold the portal's left nav to a 64px icon rail. The choice is remembered in localStorage (default expanded), read
// SYNCHRONOUSLY on the first render so the rail never jumps after load. ⚠️ Every storage access is wrapped: some privacy modes THROW on
// read or write, and a thrown storage error must never white-screen a page (the sign-in remembered-address precedent). A storage failure
// simply means the choice is not remembered.
//
// ⚠️ THE COLLAPSE ONLY EXISTS AT 1024px AND UP. Below it the nav keeps today's behaviour (the burger on a phone, the ordinary rail on a small
// laptop), so the stored choice is simply ignored there: the CSS rules and the JS handlers both gate on this one width.

export const RAIL_COLLAPSED_KEY = 'kyck-portal-nav-collapsed';
/** The width at which the collapse applies. The CSS media query (src/index.css) carries the SAME number; a test pins that they agree. */
export const RAIL_COLLAPSE_MIN_WIDTH = 1024;
export const RAIL_COLLAPSE_QUERY = `(min-width: ${RAIL_COLLAPSE_MIN_WIDTH}px)`;

export type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;

/** Only an explicit "1" is collapsed; anything else (absent, garbage, another value) is the default, expanded. */
export function parseCollapsed(raw: string | null | undefined): boolean {
  return raw === '1';
}

/** The browser's storage, or null where it is unavailable or throws on access (a private window, blocked site data, server rendering). */
export function safeStorage(): StorageLike | null {
  try {
    return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null;
  } catch {
    return null;
  }
}

/** The remembered choice. Never throws; unreadable reads as expanded. */
export function readCollapsed(storage: StorageLike | null = safeStorage()): boolean {
  if (!storage) return false;
  try {
    return parseCollapsed(storage.getItem(RAIL_COLLAPSED_KEY));
  } catch {
    return false;
  }
}

/** Remember the choice. Never throws; returns whether it was stored (the screen does not need to know, but a test does). */
export function writeCollapsed(collapsed: boolean, storage: StorageLike | null = safeStorage()): boolean {
  if (!storage) return false;
  try {
    storage.setItem(RAIL_COLLAPSED_KEY, collapsed ? '1' : '0');
    return true;
  } catch {
    return false;
  }
}

/** The collapse button's label, which names what pressing it does. */
export const collapseLabel = (collapsed: boolean): string => (collapsed ? 'Expand menu' : 'Collapse menu');

/** Is the viewport wide enough for the collapse to apply right now. No matchMedia (server rendering, jsdom) = no. */
export function collapseApplies(): boolean {
  try {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(RAIL_COLLAPSE_QUERY).matches;
  } catch {
    return false;
  }
}

export interface RectLike {
  top: number;
  right: number;
  bottom: number;
  height: number;
}

const GAP = 10;

/** A tooltip sits to the right of the row, centred on it. The page positions it with `transform: translateY(-50%)`. */
export function tipPosition(rect: RectLike): { top: number; left: number } {
  return { top: Math.round(rect.top + rect.height / 2), left: Math.round(rect.right + GAP) };
}

/**
 * A flyout opens to the right of the row, its top level with the row's, but is kept inside the viewport: if it would run off the bottom it
 * is lifted until its bottom edge is `margin` from the edge, and it never goes above `margin` from the top. An unknown height reads as the row.
 */
export function flyoutPosition(rect: RectLike, viewportHeight: number, flyoutHeight: number, margin = 12): { top: number; left: number } {
  const h = flyoutHeight > 0 ? flyoutHeight : rect.height;
  const maxTop = Math.max(margin, viewportHeight - h - margin);
  return { top: Math.round(Math.min(Math.max(rect.top, margin), maxTop)), left: Math.round(rect.right + GAP) };
}

/** One row of a flyout: a link, or (a coming-soon child) plain text. */
export interface FlyoutEntry {
  key: string;
  label: string;
  /** Absent = not a destination (never rendered as a link). */
  href?: string;
  active: boolean;
}
